import React, { useState, useEffect, useMemo } from "react";
import { Modal, Select, Button, Radio, Input, Space, Typography, DatePicker, Checkbox, Divider, message, Alert, Tooltip } from "antd";
import { SectionCard } from "../../../../../components/shared";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { fetchTemplates, Template } from "../../../../../api/communications/templateApi";
import { sendCommunication, getChannelSummary, summariseChannelOutcome } from "../../../../../api/communications/communicationApi";
import { SendCommunicationRequest, SendCommunicationResponse } from "../../../../../types/communications/CommunicationTypes";
import { createScheduledNotification } from "../../../../../api/communications/scheduledNotificationsApi";
import { fetchEmailSignatures, EmailSignature } from "../../../../../api/communications/emailSignatureApi";
import {
	NotificationRecipient,
	RELATED_ENTITY_TYPE_PATIENT,
} from "../../../../../types/communications/NotificationRecipient";
import { Company } from "../../../../../types/admin/CompanyTypes";
import { fetchClinicCompany } from "../../../../../api/admin/companyApi";
import { ChannelAvailability, DeliveryOption, firstAvailableOption, getChannelAvailability, isDeliveryOptionAvailable } from "../../../../../utils/notifications/channelAvailability";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";
import dayjs from "dayjs";

const { Text } = Typography;

interface SendManualNotificationDialogProps {
	isOpen: boolean;
	onClose: () => void;
	recipient: NotificationRecipient;
	/**
	 * CU-86d44j2ac: the entity a SCHEDULED send is filed under, and the noun the
	 * operator-facing messages use. Both default to the patient workflow.
	 */
	relatedEntityType?: string;
	recipientNoun?: string;
}

type NotificationMode = "template" | "custom";
type SendMode = "now" | "schedule";

/**
 * `CommunicationController::send()` answers 422 with this exact string when the recipient has
 * every notification channel switched off. That is the patient exercising a preference, not the
 * system breaking, and the practitioner has to be able to tell the two apart.
 */
const ALL_CHANNELS_DISABLED_ERROR = "User has all notification channels disabled";

const isAllChannelsDisabled = (error: any): boolean => error?.response?.status === 422 && error?.response?.data?.error === ALL_CHANNELS_DISABLED_ERROR;

const noEmailReason = (noun: string) => `No email address on this ${noun}'s profile`;
const noPhoneReason = (noun: string) => `No phone number on this ${noun}'s profile`;

const deliveryOptions: { value: DeliveryOption; label: string }[] = [
	{ value: "email", label: "Email" },
	{ value: "sms", label: "SMS" },
	{ value: "both", label: "Both" },
];

const unavailableReason = (option: DeliveryOption, availability: ChannelAvailability, noun: string): string => {
	if (option === "email") return noEmailReason(noun);
	if (option === "sms") return noPhoneReason(noun);
	if (!availability.email && !availability.sms) return `No email address or phone number on this ${noun}'s profile`;
	return availability.email ? noPhoneReason(noun) : noEmailReason(noun);
};

const SendManualNotificationDialog: React.FC<SendManualNotificationDialogProps> = ({
	isOpen,
	onClose,
	recipient,
	relatedEntityType = RELATED_ENTITY_TYPE_PATIENT,
	recipientNoun = "patient",
}) => {
	const [templates, setTemplates] = useState<Template[]>([]);
	const [signatures, setSignatures] = useState<EmailSignature[]>([]);
	const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
	const [selectedSignature, setSelectedSignature] = useState<number | undefined>(undefined);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [company, setCompany] = useState<Company | null>(null);
	const [mode, setMode] = useState<NotificationMode>("template");
	const [sendMode, setSendMode] = useState<SendMode>("now");
	const [customType, setCustomType] = useState<"email" | "sms" | "both">("email");
	const [templateDelivery, setTemplateDelivery] = useState<"email" | "sms" | "both">("email");

	// Custom email fields
	const [customSubject, setCustomSubject] = useState<string>("");
	const [customBody, setCustomBody] = useState<string>("");

	// Scheduling fields
	const [scheduledDate, setScheduledDate] = useState<dayjs.Dayjs | null>(null);
	const [notifySender, setNotifySender] = useState<boolean>(false);
	const [senderEmail, setSenderEmail] = useState<string>("");

	// Communication result feedback
	const [lastSendResult, setLastSendResult] = useState<SendCommunicationResponse | null>(null);

	const availability = useMemo(() => getChannelAvailability(recipient), [recipient?.email, recipient?.phone]);

	useEffect(() => {
		const loadTemplates = async () => {
			try {
				const { data: templatesData } = await fetchTemplates("send_manual_notification");
				setTemplates(templatesData.slice().sort((a: Template, b: Template) => a.name.localeCompare(b.name)));
			} catch (error) {
				console.error("Error fetching manual notification templates:", error);
			}
		};

		const loadSignatures = async (companyId: number) => {
			try {
				const signaturesData = await fetchEmailSignatures(companyId);
				setSignatures(signaturesData);

				// Set default signature if available
				const defaultSig = signaturesData.find((s) => s.is_default);
				if (defaultSig) {
					setSelectedSignature(defaultSig.id);
				}
			} catch (error) {
				console.error("Error fetching email signatures:", error);
			}
		};

		const loadCompany = async () => {
			try {
				const comp = await fetchClinicCompany();
				setCompany(comp);
				if (comp?.id) {
					loadSignatures(comp.id);
				}
			} catch (error) {
				console.error("Error fetching company data:", error);
			}
		};

		if (isOpen) {
			const user = getSessionUser();
			setSenderEmail(user.email || "");
			loadCompany();
			loadTemplates();
			// Reset form when opening
			setMode("template");
			setSendMode("now");
			setSelectedTemplate(null);
			// Deps stay [isOpen]: a profile edited mid-compose must not reset a part-written message.
			const initialDelivery = firstAvailableOption(availability) ?? "email";
			setTemplateDelivery(initialDelivery);
			setCustomType(initialDelivery);
			setCustomSubject("");
			setCustomBody("");
			setScheduledDate(null);
			setNotifySender(false);
			setLastSendResult(null);
		}
	}, [isOpen]);

	const options = templates.map((temp) => ({
		label: temp.name,
		value: temp.id,
	}));

	const signatureOptions = signatures.map((sig) => ({
		label: sig.is_default ? `${sig.name} (Default)` : sig.name,
		value: sig.id,
	}));

	const quillModules = useMemo(
		() => ({
			toolbar: [[{ header: [1, 2, 3, false] }], ["bold", "italic", "underline", "strike"], [{ list: "ordered" }, { list: "bullet" }], [{ color: [] }, { background: [] }], ["link"], ["clean"]],
		}),
		[],
	);

	// Build channels array from the active delivery selection
	const getSelectedChannels = (): ('push' | 'email' | 'sms')[] => {
		const deliveryType = mode === "template" ? templateDelivery : customType;
		switch (deliveryType) {
			case "sms":
				return ["sms"];
			case "both":
				return ["email", "sms"];
			case "email":
			default:
				return ["email"];
		}
	};

	const handleSend = async () => {
		if (!company) return;

		if (mode === "template" && !selectedTemplate) return;
		if (mode === "custom" && !customBody.trim()) return;
		if (sendMode === "schedule" && !scheduledDate) return;
		if (sendMode === "schedule" && notifySender && !senderEmail.trim()) return;

		const selectedDelivery = mode === "template" ? templateDelivery : customType;
		if (!isDeliveryOptionAvailable(selectedDelivery, availability)) {
			message.error(`${unavailableReason(selectedDelivery, availability, recipientNoun)}, so this delivery method is unavailable.`);
			return;
		}

		const channels = getSelectedChannels();

		setIsSubmitting(true);
		try {
			if (sendMode === "schedule") {
				// Schedule notification — include channels so they persist for dispatch-time fallback
				const templateId = mode === "template" && selectedTemplate ? selectedTemplate.id : undefined;

				await createScheduledNotification({
					...(templateId !== undefined && { notification_template_id: templateId }),
					...(selectedSignature !== undefined && { email_signature_id: selectedSignature }),
					recipient_email: recipient.email,
					recipient_phone: recipient.phone ?? undefined,
					recipient_user_id: recipient.id || undefined,
					scheduled_for: scheduledDate!.toISOString(),
					channels,
					data:
						mode === "custom"
							? {
									...recipient,
									custom_type: customType,
									custom_subject: customSubject || "custom notification",
									custom_body: customBody,
								}
							: recipient,
					related_entity_type: relatedEntityType,
					related_entity_id: String(recipient.id),
					company_id: company.id,
					notify_sender: notifySender,
					sender_email: notifySender ? senderEmail : undefined,
				});
				message.success("Notification scheduled successfully");
			} else {
				// Send immediately via the preference-based communication endpoint. It addresses the
				// patient by user id and `recipient_user_id` is REQUIRED, so there is no id-less
				// fallback to keep: a patient with no user account cannot be sent to at all.
				if (!recipient.id) {
					message.error(`This ${recipientNoun} has no user account yet, so a notification cannot be sent. Create their account first, or schedule the notification instead.`);
					return;
				}

				const category = mode === "template" && selectedTemplate
					? "send_manual_notification"
					: "custom_notification";

				const data = mode === "custom"
					? {
						...recipient,
						custom_type: customType,
						custom_subject: customSubject || "custom notification",
						custom_body: customBody,
					}
					: recipient;

				const result = await sendCommunication({
					recipient_user_id: recipient.id,
					category,
					...(mode === "template" && selectedTemplate ? { id: selectedTemplate.id } : {}),
					data,
					channels,
					company_id: company.id,
					email_signature_id: selectedSignature,
				});

				const { sent, total, summary } = summariseChannelOutcome(result.channels);
				const detail = summary || "the server reported no delivery channels";

				if (sent === 0 || total === 0) {
					setLastSendResult(result);
					message.error(`Nothing was sent — ${detail}`);
					// Nothing reached the patient and nothing was recorded, so the composed message must survive.
					return;
				}

				setLastSendResult(null);
				if (sent < total) {
					message.warning(`Partially sent — ${detail}`);
				} else {
					message.success(`Notification sent — ${detail}`);
				}
			}
			onClose();
		} catch (error: any) {
			console.error("Error sending/scheduling notification:", error);
			if (isAllChannelsDisabled(error)) {
				message.error(`This ${recipientNoun} has turned off every notification channel, so nothing was sent. Ask them to re-enable email or SMS in their notification preferences.`);
			} else {
				const detail = error?.response?.data?.error || error?.message || "Unknown error";
				message.error(`Failed to send notification: ${detail}`);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const isValid = !availability.none && (mode === "template" ? !!selectedTemplate : customType === "both" || customType === "email" ? !!(customSubject.trim() && customBody.trim()) : !!customBody.trim());

	const renderDeliveryOptions = () =>
		deliveryOptions.map((opt) => {
			const unavailable = !availability[opt.value];
			const button = (
				<Radio.Button key={opt.value} value={opt.value} disabled={isSubmitting || unavailable}>
					{opt.label}
				</Radio.Button>
			);
			if (!unavailable) return button;
			// A disabled Radio.Button emits no mouse events, so the Tooltip has to sit on a wrapper.
			return (
				<Tooltip key={opt.value} title={unavailableReason(opt.value, availability, recipientNoun)}>
					<span style={{ display: "inline-block", cursor: "not-allowed" }}>{button}</span>
				</Tooltip>
			);
		});

	const renderRecipientLine = () => {
		const selected = mode === "template" ? templateDelivery : customType;
		const parts = selected === "sms" ? [recipient.phone] : selected === "both" ? [recipient.email, recipient.phone] : [recipient.email];
		const to = parts.filter((part) => String(part ?? "").trim() !== "").join(" · ");
		return (
			<div>
				<Text type="secondary" style={{ fontSize: 12 }}>
					To: <strong>{to || "No contact details on file"}</strong>
				</Text>
			</div>
		);
	};

	const disablePastDates = (current: dayjs.Dayjs) => {
		return current && current < dayjs().startOf("day");
	};

	return (
		<Modal
			title="Send Notification"
			open={isOpen}
			onCancel={onClose}
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={onClose} disabled={isSubmitting}>
					Cancel
				</Button>,
				<Button key="submit" type="primary" onClick={handleSend} disabled={!isValid || isSubmitting} loading={isSubmitting}>
					{sendMode === "schedule" ? "Schedule" : "Send"}
				</Button>,
			]}
			width={800}
		>
		<Space direction="vertical" style={{ width: "100%" }} size="middle">
				{lastSendResult && (
					<Alert
						message="Nothing was sent"
						description={getChannelSummary(lastSendResult.channels) || "The server reported no delivery channels."}
						type="error"
						showIcon
						style={{ marginBottom: 0 }}
					/>
				)}

				{/* Info: channel selection & preference override */}
				{recipient.id && (
					<Alert
						message="Delivery Channels"
						description={`Your channel selection below will be used unless the ${recipientNoun} has set their own notification preferences, in which case their preferences take priority.`}
						type="info"
						showIcon
						style={{ marginBottom: 0 }}
					/>
				)}

				{/* Send timing mode */}
				<div>
					<Text strong style={{ display: "block", marginBottom: 8 }}>
						Delivery
					</Text>
					<Radio.Group value={sendMode} onChange={(e) => setSendMode(e.target.value)} disabled={isSubmitting}>
						<Radio.Button value="now">Send Now</Radio.Button>
						<Radio.Button value="schedule">Schedule for Later</Radio.Button>
					</Radio.Group>
				</div>

				{/* Scheduling options */}
				{sendMode === "schedule" && (
				<SectionCard>
						<Space direction="vertical" style={{ width: "100%" }} size="middle">
							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Schedule Date & Time <span style={{ color: "#ff4d4f" }}>*</span>
								</Text>
								<DatePicker showTime format="YYYY-MM-DD HH:mm" value={scheduledDate} onChange={setScheduledDate} disabledDate={disablePastDates} style={{ width: "100%" }} placeholder="Select date and time" />
							</div>

							<Divider style={{ margin: "8px 0" }} />

							<div>
								<Checkbox checked={notifySender} onChange={(e) => setNotifySender(e.target.checked)}>
									Notify me when this notification is sent
								</Checkbox>
							</div>

							{notifySender && (
								<div>
									<Text strong style={{ display: "block", marginBottom: 8 }}>
										Your Email <span style={{ color: "var(--mg-danger)" }}>*</span>
									</Text>
									<Input placeholder="Your email address" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} type="email" />
								</div>
							)}
						</Space>
					</SectionCard>
				)}

				{/* Notification type selector */}
				<div>
					<Text strong style={{ display: "block", marginBottom: 8 }}>
						Notification Type
					</Text>
					<Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} disabled={isSubmitting}>
						<Radio.Button value="template">Use Template</Radio.Button>
						<Radio.Button value="custom">Custom</Radio.Button>
					</Radio.Group>
				</div>

				{/* Template mode */}
				{mode === "template" && (
				<SectionCard>
						<Space direction="vertical" style={{ width: "100%" }} size="middle">
							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Select Template
								</Text>
								<Select
									id="manualNotificationSelector"
									value={selectedTemplate?.id}
									options={options}
									onChange={(value) => {
										const selected = templates.find((t) => t.id === value);
										setSelectedTemplate(selected || null);
									}}
									placeholder="Select a notification template"
									style={{ width: "100%" }}
									disabled={isSubmitting}
									showSearch
									filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
								/>
							</div>

							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Send As
								</Text>
								<Radio.Group
									value={templateDelivery}
									onChange={(e) => {
										setTemplateDelivery(e.target.value);
										setLastSendResult(null);
									}}
								>
									{renderDeliveryOptions()}
								</Radio.Group>
							</div>

							{renderRecipientLine()}

							{templateDelivery !== "sms" && signatures.length > 0 && (
								<div>
									<Text strong style={{ display: "block", marginBottom: 8 }}>
										Email Signature
									</Text>
									<Select value={selectedSignature} options={signatureOptions} onChange={setSelectedSignature} placeholder="Select email signature (optional)" style={{ width: "100%" }} disabled={isSubmitting} allowClear />
								</div>
							)}
						</Space>
					</SectionCard>
				)}

				{/* Custom email mode */}
				{mode === "custom" && (
					<SectionCard>
						<Space direction="vertical" style={{ width: "100%" }} size="middle">
							{/* Recipient info */}
							<Radio.Group
								value={customType}
								onChange={(e) => {
									setCustomType(e.target.value);
									setLastSendResult(null);
								}}
							>
								{renderDeliveryOptions()}
							</Radio.Group>
							{renderRecipientLine()}

							{/* Subject field */}
							{customType !== "sms" && (
								<>
									<div>
										<Text strong style={{ display: "block", marginBottom: 8 }}>
											Subject <span style={{ color: "var(--mg-danger)" }}>*</span>
										</Text>
										<Input placeholder="Enter email subject" value={customSubject} onChange={(e) => setCustomSubject(e.target.value)} disabled={isSubmitting} maxLength={200} />
									</div>

									{signatures.length > 0 && (
										<div>
											<Text strong style={{ display: "block", marginBottom: 8 }}>
												Email Signature
											</Text>
											<Select value={selectedSignature} options={signatureOptions} onChange={setSelectedSignature} placeholder="Select email signature (optional)" style={{ width: "100%" }} disabled={isSubmitting} allowClear />
										</div>
									)}
								</>
							)}

							{/* Email body */}
							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Message <span style={{ color: "var(--mg-danger)" }}>*</span>
								</Text>
								<div
									style={{
										border: "1px solid var(--mg-border)",
										borderRadius: 6,
										overflow: "hidden",
									}}
								>
									<ReactQuill
										theme="snow"
										value={customBody}
										onChange={setCustomBody}
										modules={quillModules}
										placeholder="Write your message here..."
										style={{
											minHeight: 300,
											background: "white",
										}}
										readOnly={isSubmitting}
									/>
								</div>
							</div>

							{/* Helper text */}
							<div
								style={{
									padding: 12,
									background: "var(--mg-bg-subtle)",
									borderRadius: 6,
									border: "1px solid var(--mg-border)",
								}}
							>
								<Text type="secondary" style={{ fontSize: 12 }}>
									💡 You can use formatting tools above to style your email. The {recipientNoun} will receive this as an HTML email.
								</Text>
							</div>
						</Space>
					</SectionCard>
				)}
			</Space>
		</Modal>
	);
};

export default SendManualNotificationDialog;
